
import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { ProjectList } from './pages/ProjectList.tsx';
import { ProjectDetail } from './pages/ProjectDetail.tsx';
import { Settings } from './pages/Settings.tsx';
import { api } from './services/api.ts';
import { ToastContainer } from './components/Toast.tsx';
import { useToast } from './hooks/useToast.ts';
import { AIChatbot } from './components/AIChatbot.tsx';
import { OnboardingGate } from './components/OnboardingGate.tsx';
import { AuthGate } from './components/AuthGate.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { KnowledgeBase } from './components/KnowledgeBase.tsx';

import { SyncManager } from './services/syncService.ts';

const App: React.FC = () => {
  const { messages, removeToast } = useToast();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Smart seeding: only seed if not already done
      const hasSeeded = localStorage.getItem('lc_seeded');
      if (!hasSeeded) {
        try {
          await api.seed();
          localStorage.setItem('lc_seeded', 'true');
        } catch (err) {
          console.error('Seeding failed:', err);
        }
      }
      setIsReady(true);
    };
    init();

    // Start Cloud Sync (Offline-First)
    const syncManager = SyncManager.getInstance();
    syncManager.startAutoSync(30000); // Sync every 30s
    return () => syncManager.stopAutoSync();
  }, []);

  if (!isReady) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-zinc-200 border-t-black rounded-full animate-spin mx-auto"></div>
          <p className="text-xs text-zinc-400 mt-4 font-medium">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Router>
        <AuthGate>
          <OnboardingGate>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/knowledge-base" element={<KnowledgeBase />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
            <AIChatbot />
            <ToastContainer messages={messages} remove={removeToast} />
          </OnboardingGate>
        </AuthGate>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
